import type Konva from 'konva';

import { Box } from './types';

type BuildArcTopPathArgs = {
  xLeft: number;
  xRight: number;
  yBottom: number;
  yTop: number;
  topEdge?: { cx: number; cy: number; r: number };
  arcInset?: number;
};

/**
 * SVG `d` for a rectangle-with-optional-arc-top.
 *
 * The parallel offset of a circular arc by a constant `arcInset` is another
 * arc with the same center and radius `r - arcInset` — so the offset arc
 * keeps `topEdge.cx` / `topEdge.cy` and just shrinks. The side walls (given
 * by `xLeft` / `xRight`) meet the offset arc at
 * `y = cy + sqrt((r - arcInset)² - (xWall - cx)²)`.
 *
 * Bounds (`xLeft`, `xRight`, `yBottom`) are given explicitly so callers can
 * use asymmetric side insets (e.g. floating-mullion sashes where one side
 * butts up against the mullion without a margin).
 *
 * When the offset arc meets a side wall below `yBottom` (shallow base under
 * the arc, e.g. a small heightBaseOfArc above a transom), the shape is
 * clipped at `yBottom`: the arc starts/ends where it crosses `y = yBottom`
 * instead of at the wall, so it never dips below the bottom edge.
 *
 * Without `topEdge` the shape is a plain rectangle and `arcInset` is unused.
 */
export function buildArcTopPath({
  xLeft,
  xRight,
  yBottom,
  yTop,
  topEdge,
  arcInset = 0,
}: BuildArcTopPathArgs): string {
  if (!topEdge) {
    return `M ${xLeft} ${yBottom} L ${xRight} ${yBottom} L ${xRight} ${yTop} L ${xLeft} ${yTop} Z`;
  }

  const innerR = topEdge.r - arcInset;
  const { left, right } = arcWallJoints({
    xLeft,
    xRight,
    yBottom,
    cx: topEdge.cx,
    cy: topEdge.cy,
    r: innerR,
  });

  return (
    `M ${left.x} ${yBottom} ` +
    `L ${right.x} ${yBottom} ` +
    `L ${right.x} ${right.y} ` +
    `A ${innerR} ${innerR} 0 0 1 ${left.x} ${left.y} Z`
  );
}

// Where the arc meets each side wall — or, if that intersection falls below
// yBottom, where the arc crosses y = yBottom (clamped inward so the shape
// never extends below its bottom edge).
function arcWallJoints({
  xLeft,
  xRight,
  yBottom,
  cx,
  cy,
  r,
}: {
  xLeft: number;
  xRight: number;
  yBottom: number;
  cx: number;
  cy: number;
  r: number;
}) {
  const yArcAt = (x: number) =>
    cy + Math.sqrt(Math.max(0, r * r - (x - cx) ** 2));
  const xHalfSpanAtBottom = Math.sqrt(Math.max(0, r * r - (yBottom - cy) ** 2));

  const joint = (xWall: number, sign: 1 | -1) => {
    const yWall = yArcAt(xWall);
    if (yWall >= yBottom) return { x: xWall, y: yWall };
    return { x: cx + sign * xHalfSpanAtBottom, y: yBottom };
  };

  return { left: joint(xLeft, -1), right: joint(xRight, 1) };
}

type ArcTopClipArgs = {
  box: Box;
  topEdge: { cx: number; cy: number; r: number };
};

// Builds a Konva clipFunc that traces the inner-arc-top shape of `box`.
// `topEdge.{cx,cy,r}` must be expressed in the same coordinate frame as
// `box.{x,y}` (i.e. the parent Group's local coords). The sweep direction
// matches buildArcTopPath's SVG sweep-flag=1 under the stage's scaleY=-1.
export function makeArcTopClipFunc({ box, topEdge }: ArcTopClipArgs) {
  const { x, y, width } = box;
  const { cx, cy, r } = topEdge;
  const { left, right } = arcWallJoints({
    xLeft: x,
    xRight: x + width,
    yBottom: y,
    cx,
    cy,
    r,
  });
  const startAngle = Math.atan2(right.y - cy, right.x - cx);
  const endAngle = Math.atan2(left.y - cy, left.x - cx);
  return (ctx: Konva.Context) => {
    ctx.moveTo(left.x, y);
    ctx.lineTo(right.x, y);
    ctx.lineTo(right.x, right.y);
    ctx.arc(cx, cy, r, startAngle, endAngle, false);
    ctx.closePath();
  };
}
